import { useQuery } from '@tanstack/react-query';
import { clientService } from '@services/client';
import { useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import { ClientProjectOverview } from '@models/client.model';
import { isExternalViewerRole } from '@constants/roleHierarchy';

export const CLIENT_PROJECTS_OVERVIEW_QUERY_KEY = 'clientProjectsOverview';

/**
 * Sprint 44, story-226. The one source of every progress number on the client home, the project
 * progress header and the attention block. Always called with explicit ids (never `undefined`
 * meaning "all") so the key is stable and shared by every surface on a page. Runs for CLIENT and
 * REVIEWER, or for an internal role only when `enabled` is passed (an OWNER previewing).
 * Refetches on window focus and every 60 seconds so a client sees yesterday's work arrive; the
 * previous numbers stay on screen during a background refetch. No toast: each surface renders
 * its own quiet error line.
 */
export const useClientProjectsOverview = (
  projectIds: string[],
  options: { enabled?: boolean } = {},
) => {
  const organizationId = useAppSelector(
    (state: RootState) => state.organization.organizationId,
  );
  const role = useAppSelector((state: RootState) => state.user.role);
  const sortedIds = [...projectIds].sort();

  return useQuery<ClientProjectOverview[], Error>({
    queryKey: [CLIENT_PROJECTS_OVERVIEW_QUERY_KEY, organizationId, sortedIds],
    queryFn: () => clientService.getClientProjectsOverview(sortedIds),
    enabled:
      !!organizationId &&
      sortedIds.length > 0 &&
      (isExternalViewerRole(role) || !!options.enabled),
    refetchOnWindowFocus: true,
    refetchInterval: 60_000,
    retry: 1,
  });
};

export default useClientProjectsOverview;
